import { useCallback, useEffect, useRef, useState } from 'react'
import { F0Dialog } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import i18n from 'modules/core/lib/i18n'
import { useTicketingPolicies } from 'modules/ticketing/hooks/useTicketingPolicies'
import { trackOpenCreateTicketDialog } from 'modules/ticketing/lib/tracking'

import { CreateWithAiTab } from './CreateWithAiTab'
import { ManualFormTab, type ManualFormInitialValues } from './ManualFormTab'

interface CreateTicketDialogProps {
  isOpen: boolean
  onClose: () => void
  initialValues?: ManualFormInitialValues
  onLaunchAiTicketCreation: (
    message: string,
    options?: { source?: 'description' | 'category' }
  ) => void
}

export interface CreateTicketTabHandle {
  submit: () => void
}

export interface CreateTicketTabSubmitState {
  disabled: boolean
  loading?: boolean
}

const hasInitialValues = (initialValues?: ManualFormInitialValues) =>
  Boolean(
    initialValues?.title ||
    initialValues?.description ||
    initialValues?.categoryId ||
    initialValues?.categoryName ||
    initialValues?.priority
  )

const CREATE_WITH_AI_TAB = 'create-with-ai'
const USE_FORM_TAB = 'use-form'
const DEFAULT_SUBMIT_STATE = { disabled: true, loading: false }

export const CreateTicketDialog = ({
  isOpen,
  onClose,
  initialValues,
  onLaunchAiTicketCreation,
}: CreateTicketDialogProps) => {
  const { aiEnabled } = useTicketingPolicies()
  const canUseAiTab = aiEnabled === true
  const shouldOpenManualForm = hasInitialValues(initialValues)
  const aiTabRef = useRef<CreateTicketTabHandle>(null)
  const formTabRef = useRef<CreateTicketTabHandle>(null)
  const wasOpenRef = useRef(false)
  const [activeTab, setActiveTab] = useState(
    canUseAiTab && !shouldOpenManualForm ? CREATE_WITH_AI_TAB : USE_FORM_TAB
  )
  const [aiSubmitState, setAiSubmitState] =
    useState<CreateTicketTabSubmitState>(DEFAULT_SUBMIT_STATE)
  const [formSubmitState, setFormSubmitState] =
    useState<CreateTicketTabSubmitState>(DEFAULT_SUBMIT_STATE)

  const isAiTabActive = canUseAiTab && activeTab === CREATE_WITH_AI_TAB
  const activeSubmitState = isAiTabActive ? aiSubmitState : formSubmitState
  const handleSubmit = useCallback(() => {
    if (isAiTabActive) {
      aiTabRef.current?.submit()
    } else {
      formTabRef.current?.submit()
    }
  }, [isAiTabActive])

  const primaryAction = {
    label: isAiTabActive
      ? i18n.t('ticketing.inbox.create.ai_submit_button')
      : i18n.t('ticketing.inbox.create.create_button'),
    onClick: handleSubmit,
    disabled: activeSubmitState.disabled,
    loading: activeSubmitState.loading,
  }

  useEffect(() => {
    const opened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen

    if (opened) {
      trackOpenCreateTicketDialog()
      setActiveTab(canUseAiTab && !shouldOpenManualForm ? CREATE_WITH_AI_TAB : USE_FORM_TAB)
    }
  }, [canUseAiTab, isOpen, shouldOpenManualForm])

  return (
    <F0Dialog
      isOpen={isOpen}
      module={{ id: 'inbox', label: i18n.t('ticketing.inbox.create.new_ticket') }}
      onClose={onClose}
      primaryAction={primaryAction}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      {canUseAiTab && (
        <Tabs
          tabs={[
            { id: CREATE_WITH_AI_TAB, label: i18n.t('ticketing.inbox.create.ai_tab') },
            { id: USE_FORM_TAB, label: i18n.t('ticketing.inbox.create.form_tab') },
          ]}
          activeTabId={activeTab}
          setActiveTabId={setActiveTab}
        />
      )}

      {isAiTabActive ? (
        <CreateWithAiTab
          ref={aiTabRef}
          active={isAiTabActive}
          isOpen={isOpen}
          onLaunchAiTicketCreation={onLaunchAiTicketCreation}
          onSubmitStateChange={setAiSubmitState}
        />
      ) : (
        <ManualFormTab
          ref={formTabRef}
          isOpen={isOpen}
          initialValues={initialValues}
          onClose={onClose}
          onSubmitStateChange={setFormSubmitState}
        />
      )}
    </F0Dialog>
  )
}
